import { DestroyRef, Injectable, inject, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning';

export interface ToastMessage {
  readonly id: number;
  readonly messageKey: string;
  readonly type: ToastType;
  readonly actionLabelKey: string | null;
}

const TOAST_DURATION_MS = 5000;

export const TOAST_ACTION_DURATION_MS = 30000;

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly toast = signal<ToastMessage | null>(null);
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private nextId = 1;

  readonly currentToast = this.toast.asReadonly();

  constructor() {
    this.destroyRef.onDestroy(() => this.clearTimeout());
  }

  show(messageKey: string, type: ToastType = 'success', duration = TOAST_DURATION_MS): void {
    this.setToast({ messageKey, type, duration, actionLabelKey: null });
  }

  showWithAction(
    messageKey: string,
    actionLabelKey: string,
    type: ToastType = 'warning',
    duration = TOAST_ACTION_DURATION_MS,
  ): void {
    this.setToast({ messageKey, type, duration, actionLabelKey });
  }

  dismiss(): void {
    this.clearTimeout();
    this.toast.set(null);
  }

  private setToast(options: {
    messageKey: string;
    type: ToastType;
    duration: number;
    actionLabelKey: string | null;
  }): void {
    this.clearTimeout();
    this.toast.set({
      id: this.nextId++,
      messageKey: options.messageKey,
      type: options.type,
      actionLabelKey: options.actionLabelKey,
    });
    this.timeoutId = setTimeout(() => this.dismiss(), options.duration);
  }

  private clearTimeout(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
